'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import DeviceTable from './DeviceTable';
import DeviceFilters from './DeviceFilters';
import DeviceDetailPanel from './DeviceDetailPanel';
import DeviceWizard from './DeviceWizard';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export interface Device {
  id: string;
  hostname: string;
  categoria: string;
  stato: 'online' | 'offline' | 'latenza_alta' | 'intermittente' | 'manutenzione';
  ip_address: string | null;
  mac_address: string | null;
  numero_seriale: string;
  modello: string;
  firmware: string;
  reparto: string;
  responsabile: string;
  note: string;
  foto_url: string;
  ip_type: 'statico' | 'dhcp';
  ping_ms: number | null;
  uptime_ore: number;
  ultima_visita: string | null;
  created_at: string;
  subnet?: { nome: string; cidr: string } | null;
  vlan?: { nome: string; vlan_id: number } | null;
}

export default function DeviceInventoryContent() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [repartoFilter, setRepartoFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [sortCol, setSortCol] = useState('hostname');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; ids: string[] }>({ open: false, ids: [] });
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageSize = 10;
  const { canWrite } = useAuth();

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('devices')
        .select('*, subnet:subnets(nome, cidr), vlan:vlans(nome, vlan_id)')
        .order('hostname', { ascending: true });
      if (error) throw error;
      setDevices((data || []) as Device[]);
    } catch (err: any) {
      toast.error('Errore nel caricamento dispositivi: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();

    // Real-time subscription
    const channel = supabase
      .channel('devices_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'devices' }, () => {
        fetchDevices();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchDevices]);

  const filtered = useMemo(() => {
    return devices
      .filter((d) => {
        const q = search.toLowerCase();
        const matchSearch = !q ||
          d.hostname.toLowerCase().includes(q) ||
          (d.ip_address || '').includes(q) ||
          (d.mac_address || '').toLowerCase().includes(q) ||
          d.reparto.toLowerCase().includes(q) ||
          d.modello.toLowerCase().includes(q) ||
          d.responsabile.toLowerCase().includes(q);
        const matchCat = categoriaFilter === 'all' || d.categoria === categoriaFilter;
        const matchStatus = statusFilter === 'all' || d.stato === statusFilter;
        const matchReparto = repartoFilter === 'all' || d.reparto === repartoFilter;
        return matchSearch && matchCat && matchStatus && matchReparto;
      })
      .sort((a, b) => {
        const av = (a as any)[sortCol] || '';
        const bv = (b as any)[sortCol] || '';
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
  }, [devices, search, categoriaFilter, statusFilter, repartoFilter, sortCol, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  const reparti = useMemo(() => [...new Set(devices.map(d => d.reparto).filter(Boolean))], [devices]);

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleAll = () => {
    if (selectedIds.size === pageData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pageData.map(d => d.id)));
  };

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = () => {
    setDeleteConfirm({ open: true, ids: Array.from(selectedIds) });
  };

  const confirmDelete = async () => {
    const ids = deleteConfirm.ids;
    setDeleteConfirm({ open: false, ids: [] });
    try {
      const { error } = await supabase.from('devices').delete().in('id', ids);
      if (error) throw error;
      toast.success(`${ids.length} dispositivo/i eliminato/i`);
      setSelectedIds(new Set());
      if (selectedDevice && ids.includes(selectedDevice.id)) setSelectedDevice(null);
      fetchDevices();
    } catch (err: any) {
      toast.error('Errore eliminazione: ' + err.message);
    }
  };

  const handleDeviceAdded = () => {
    setWizardOpen(false);
    fetchDevices();
    toast.success('Dispositivo aggiunto con successo!');
  };

  const handleExport = () => {
    if (devices.length === 0) return;
    const headers = ['Hostname', 'Categoria', 'Stato', 'IP Address', 'MAC Address', 'Reparto', 'Responsabile', 'Modello', 'Numero Seriale', 'Firmware', 'Ultima Visita'];
    const rows = devices.map(d => [
      d.hostname,
      d.categoria,
      d.stato,
      d.ip_address || '',
      d.mac_address || '',
      d.reparto,
      d.responsabile,
      d.modello,
      d.numero_seriale,
      d.firmware,
      d.ultima_visita ? new Date(d.ultima_visita).toLocaleDateString('it-IT') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventario_dispositivi_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error('Il file CSV è vuoto o non valido.');
        return;
      }
      // Parse header (first line)
      const header = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
      const rows = lines.slice(1);
      const devices = rows.map(row => {
        const cols = row.split(',').map(c => c.replace(/"/g, '').trim());
        const obj: Record<string, any> = {};
        header.forEach((h, i) => { obj[h] = cols[i] || ''; });
        return {
          hostname: obj['hostname'] || obj['nome'] || 'Dispositivo',
          categoria: obj['categoria'] || 'altro',
          stato: (obj['stato'] || 'offline') as any,
          ip_address: obj['ip_address'] || obj['ip'] || null,
          mac_address: obj['mac_address'] || obj['mac'] || null,
          numero_seriale: obj['numero_seriale'] || obj['seriale'] || '',
          modello: obj['modello'] || '',
          firmware: obj['firmware'] || '',
          reparto: obj['reparto'] || '',
          responsabile: obj['responsabile'] || '',
          note: obj['note'] || '',
          ip_type: (obj['ip_type'] || 'dhcp') as any,
        };
      }).filter(d => d.hostname);

      if (devices.length === 0) {
        toast.error('Nessun dispositivo valido trovato nel CSV.');
        return;
      }

      const { error } = await supabase.from('devices').insert(devices);
      if (error) throw error;
      toast.success(`${devices.length} dispositivi importati con successo!`);
      fetchDevices();
    } catch (err: any) {
      toast.error('Errore importazione: ' + err.message);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const offlineCount = devices.filter(d => d.stato === 'offline').length;
  const onlineCount = devices.filter(d => d.stato === 'online').length;

  return (
    <div className="min-h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* Hidden file input for CSV import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleImportCSV}
        className="hidden"
      />

      {wizardOpen && (
        <DeviceWizard
          onClose={() => setWizardOpen(false)}
          onSuccess={handleDeviceAdded}
        />
      )}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Elimina dispositivi"
        message={`Sei sicuro di voler eliminare ${deleteConfirm.ids.length} dispositivo/i? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, ids: [] })}
        danger
      />

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6 space-y-5">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Inventario Dispositivi' }]} />
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white">Inventario Dispositivi</h1>
            <p className="text-base text-secondary-color mt-1">
              {loading ? 'Caricamento...' : (
                <>
                  {devices.length} dispositivi gestiti ·{' '}
                  <span className="text-green-400 font-semibold">{onlineCount} online</span> ·{' '}
                  <span className="text-red-400 font-semibold">{offlineCount} offline</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={devices.length === 0}
              className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all duration-150 disabled:opacity-50"
            >
              <Icon name="ArrowDownTrayIcon" size={17} />
              Esporta CSV
            </button>
            {canWrite() && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading}
                className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all duration-150 disabled:opacity-50"
              >
                <Icon name="ArrowUpTrayIcon" size={17} />
                {importLoading ? 'Importazione...' : 'Importa CSV'}
              </button>
            )}
            {canWrite() && (
              <button
                onClick={() => setWizardOpen(true)}
                className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg px-5 py-2.5 text-sm font-bold transition-all active:scale-95"
              >
                <Icon name="PlusIcon" size={18} />
                Aggiungi Dispositivo
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <DeviceFilters
          search={search} onSearch={(v) => { setSearch(v); setPage(1); }}
          categoriaFilter={categoriaFilter} onCategoriaFilter={(v) => { setCategoriaFilter(v); setPage(1); }}
          statusFilter={statusFilter} onStatusFilter={(v) => { setStatusFilter(v); setPage(1); }}
          repartoFilter={repartoFilter} onRepartoFilter={(v) => { setRepartoFilter(v); setPage(1); }}
          reparti={reparti}
        />

        <div className="flex gap-5">
          {/* Table */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="bg-surface border border-border rounded-xl p-16 flex flex-col items-center gap-4">
                <svg className="animate-spin w-8 h-8 text-sky-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className="text-base text-secondary-color">Caricamento dispositivi...</p>
              </div>
            ) : (
              <DeviceTable
                data={pageData}
                selectedIds={selectedIds}
                onToggleAll={toggleAll}
                onToggleRow={toggleRow}
                onSelectDevice={setSelectedDevice}
                selectedDevice={selectedDevice}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={toggleSort}
                page={page}
                totalPages={totalPages}
                totalCount={filtered.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onDeleteSelected={handleDeleteSelected}
                canWrite={canWrite()}
              />
            )}
          </div>

          {/* Detail panel */}
          {selectedDevice && (
            <div className="w-80 flex-shrink-0">
              <DeviceDetailPanel
                device={selectedDevice}
                onClose={() => setSelectedDevice(null)}
                onRefresh={fetchDevices}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}